import { Input } from '@/components/ui/input';
import { Loader2, Search, MapPin, CircleCheck } from 'lucide-react';
import type { GolfCourseApiSearchResult } from '@/types/golfCourseApi';

interface LocalCourseResult {
  id: string;
  name: string;
  city: string;
  state: string;
  api_course_id: number | null;
}

interface CourseSearchStepProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  results: GolfCourseApiSearchResult[];
  searching: boolean;
  loadingDetails: boolean;
  onSelect: (result: GolfCourseApiSearchResult) => void;
  autoFocus?: boolean;
  localResults?: LocalCourseResult[];
  onSelectLocal?: (course: LocalCourseResult) => void;
}

const CourseSearchStep = ({
  searchQuery,
  onSearchChange,
  results,
  searching,
  loadingDetails,
  onSelect,
  autoFocus = false,
  localResults = [],
  onSelectLocal,
}: CourseSearchStepProps) => {
  const trimmedQuery = searchQuery.trim();
  const hasQuery = trimmedQuery.length >= 2;
  const hasLocalResults = localResults.length > 0;
  const hasApiResults = results.length > 0;
  const showBothSections = hasLocalResults && hasApiResults;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by course name..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className="pl-9"
          autoFocus={autoFocus}
        />
      </div>

      {searching && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Searching courses...</span>
        </div>
      )}

      {loadingDetails && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading course details...</span>
        </div>
      )}

      {!searching && !loadingDetails && hasQuery && (
        <div className="max-h-72 overflow-y-auto space-y-3">
          {/* Local results section */}
          {hasLocalResults && onSelectLocal && (
            <div>
              {showBothSections && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-3 mb-1">
                  Already in Birdie Chase
                </p>
              )}
              <div className="space-y-1">
                {localResults.map(course => (
                  <button
                    key={course.id}
                    onClick={() => onSelectLocal(course)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-2"
                  >
                    <CircleCheck className="h-4 w-4 text-forest shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{course.name}</div>
                      <div className="flex items-center text-xs text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                        {[course.city, course.state].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* API results section */}
          {hasApiResults && (
            <div>
              {showBothSections && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-3 mb-1">
                  Search Results
                </p>
              )}
              <div className="space-y-1">
                {results.map(result => (
                  <button
                    key={result.id}
                    onClick={() => onSelect(result)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors"
                  >
                    <div className="font-medium text-sm">
                      {result.course_name || result.club_name}
                    </div>
                    {result.location && (
                      <div className="flex items-center text-xs text-muted-foreground mt-0.5">
                        <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                        {[result.location.city, result.location.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No API results but local results exist */}
          {!hasApiResults && hasLocalResults && (
            <p className="text-sm text-muted-foreground text-center py-2">
              No additional courses found in the course database.
            </p>
          )}
        </div>
      )}

      {/* No results at all */}
      {!searching && !loadingDetails && hasQuery && !hasLocalResults && !hasApiResults && (
        <div className="text-sm text-muted-foreground py-4 px-3">
          <p className="text-center mb-2">
            No courses found for "{trimmedQuery}"
          </p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Try a shorter name (e.g., "Pebble" instead of "Pebble Beach Golf Links")</li>
            <li>Check the spelling of the course name</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default CourseSearchStep;
